import React, { useState } from 'react';
import { useToast } from '@withjoy/joykit';
import { WarningTriangle } from '@withjoy/joykit/icons';
import { useTranslation } from '@shared/core';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useRemoveStayZoneMutation } from '@graphql/generated';
import type { StayZoneAreaData } from '@shared/components/StayZoneTile/StayZoneTile.types';
import { addError } from '@shared/utils/logger';

type UseRemoveStayZoneReturn = Readonly<{
  /** Stay zone pending user confirmation in the delete dialog, or `null` when dialog is closed */
  stayZoneToDelete: StayZoneAreaData | null;
  /** IDs optimistically removed while mutations are in-flight — supports concurrent deletions */
  deletedIds: ReadonlySet<string>;
  handleRemove: (stayZone: StayZoneAreaData) => void;
  handleCloseDialog: () => void;
  handleConfirmRemove: () => void;
}>;

export const useRemoveStayZone = (): UseRemoveStayZoneReturn => {
  const { tq } = useTranslation('accommodationsAdmin');
  const { toast } = useToast();

  const [stayZoneToDelete, setStayZoneToDelete] = useState<StayZoneAreaData | null>(null);
  // Mutable Set — intentional exception to tracks optimistically removed zones with intentional mutation for performance
  const [deletedIds, setDeletedIds] = useState<Set<string>>(new Set());

  const [removeStayZone] = useRemoveStayZoneMutation();

  const handleRemove = useEventCallback((stayZone: StayZoneAreaData) => {
    setStayZoneToDelete(stayZone);
  });

  const handleCloseDialog = useEventCallback(() => {
    setStayZoneToDelete(null);
  });

  const handleConfirmRemove = useEventCallback(async () => {
    // Type narrowing for TypeScript (stayZoneToDelete is guaranteed non-null by dialog)
    if (!stayZoneToDelete) return;

    const { id } = stayZoneToDelete;

    setStayZoneToDelete(null);
    // New Set reference required for React re-render; spread not available without downlevelIteration
    setDeletedIds(prev => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });

    try {
      await removeStayZone({
        variables: { payload: { id } },
        refetchQueries: ['StayZones'],
      });
      toast(tq`Stay zone removed successfully.`);
    } catch (error) {
      setStayZoneToDelete(stayZoneToDelete); // Reopen dialog
      toast(tq`An error occurred. Please try again.`, { icon: <WarningTriangle /> });
      addError(error, { context: 'useRemoveStayZone', message: 'Failed to remove stay zone' });
    }
  });

  return {
    stayZoneToDelete,
    deletedIds,
    handleRemove,
    handleCloseDialog,
    handleConfirmRemove,
  };
};
